import { captureException } from "@utils/sentry";

import { getCookie } from "./cookie";

interface ParselyEntry {
  author: string | null;
  authors: [] | null;
  image_url: string | null;
  link: string | null;
  metadata: string | null;
  pub_date: string | null;
  section: string | null;
  tags: [] | null;
  thumb_url_medium: string | null;
  title: string | null;
  url: string | null;
}

export type ParselyData = ParselyEntry[];

export const transformParselyData = (
  data: ParselyData,
  retainAbsoluteUrls: boolean = false,
) => {
  return data
    .filter((entry) => entry.pub_date)
    .map((entry, idx) => ({
      author: { fullName: entry.author },
      image_url: entry.image_url,
      publishDate: entry.pub_date,
      tags: entry.tags,
      title: entry.title,
      url: entry.url
        ? retainAbsoluteUrls // Keep absolute urls for feed-only communities
          ? entry.url
          : new URL(entry.url).pathname
        : undefined,
      uid: idx.toString(),
    }));
};

export type ParselyResult = ReturnType<typeof transformParselyData>;

const PARSELY_SESSION_COOKIE_NAME = "_parsely_visitor";

// Get a UUID to send to Parsely based on Google ID, and falling back to their
// own uuid if not available or if user does not consent to sharing it.
// Using Google's ID lets us have consistent recommendations across devices rather
// than tied to the device/browser as the Parsely uuid would be
export const getParselyUUID = (userId: string | null) => {
  const consentCookieVal = getCookie("OptanonConsent");

  if (userId && consentCookieVal.includes("C0002:1")) {
    // User is logged in and has given consent to performance cookies
    return userId;
  }

  // It's possible to get the UUID for the user from the Parsely config object on
  // the client: `PARSELY.config.parsely_site_uuid`. This config can take a
  // moment to load, though, and it's faster to get it directly from the Parsely
  // cookie.
  const parselyCookieVal = getCookie(PARSELY_SESSION_COOKIE_NAME);

  try {
    const parselyConfig = JSON.parse(parselyCookieVal) as { id?: string };
    return parselyConfig.id;
  } catch {
    return null;
  }
};

export const trainParselyProfile = async (
  communityDomain: string,
  uuid: string,
  url: string,
) => {
  const domain = communityDomain.replace(/^www\./, "");

  try {
    const resp = await fetch(
      `https://api.parsely.com/v2/profile?apikey=${domain}&uuid=${encodeURIComponent(
        uuid,
      )}&url=${encodeURIComponent(url)}`,
    );
    if (!resp.ok) {
      throw Error(`${resp.status} response from parsely API`);
    }
  } catch (err) {
    captureException(err, {
      tags: {
        context: "parsely",
      },
      extra: {
        message: "failed to send training data to Parsely",
      },
    });
  }
};

export const mapParselyDataToContentCardData = (
  data: ParselyResult | null | undefined,
) => {
  return data?.map((article) => ({
    authors: [{ name: article.author.fullName || "" }],
    headline: article.title || "",
    timestamp: article.publishDate || "",
    thumbnails: {
      horizontal: article.image_url || "",
      square: article.image_url || "",
    },
    id: article.url || "",
    globalId: "",
    permalink: article.url || "",
    contentType: "post",
    site: null,
  }));
};
